<script setup lang="ts">
/**
 * Gift quiz "Shopping for someone?" — full-screen layer (Figma Quizzes › Gift quiz ·
 * desktop 3222:17260 / mobile 3222:105807). Occasion, budget and note are asked every
 * time; the person's styles / colors / brands are prefilled from the last gift for them.
 * The ideas come from the catalogue search (BE-153); the result opens the Gift ideas page.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useFairCompact } from '@/composables/useFairCompact'
import { useCreatePersonMutation } from '@/mutations/profile/usePeopleMutations'
import { useAvailableBrandsQuery, useAvailableColorsQuery, useAvailableStylesQuery } from '@/queries/my-style'
import { usePeopleQuery } from '@/queries/profile/usePeopleQuery'
import type { Person } from '@/types/people'
import {
  FairQuizHeader,
  FairQuizLayer,
  FairQuizLeaveDialog,
  FairQuizStep,
  FairQuizWelcome,
  settleFairQuizHistory,
} from '@/ui/patterns/FairQuiz'
import { useFairToast } from '@/ui/patterns/FairToast'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairChip } from '@/ui/primitives/FairChip'
import { FairInputText } from '@/ui/primitives/FairInputText'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'
import { FairThemeCard } from '@/ui/primitives/FairThemeCard'
import { readStoredCurrency } from '@/ui/tokens/fair-locale-menu.data'
import FairProfileV2SelectionPicker from '@/widgets/profile/fair-v2/FairProfileV2SelectionPicker.vue'
import type { FairProfileV2PickerItem } from '@/widgets/profile/fair-v2/fair-profile-v2-my-style.data'
import {
  emptyFairProfileV2PersonDraft,
  fairProfileV2VisiblePeople,
  isFairProfileV2PersonComplete,
  toFairProfileV2PersonPayload,
  writeFairProfileV2ChildSex,
  type FairProfileV2PersonDraft,
} from '@/widgets/profile/fair-v2/fair-profile-v2-people.data'
import { mapFairSearchV2TeaserToGridCard } from '@/widgets/search/fair-v2/fair-search-v2-recs.data'
import type { FairSearchV2ProductCard } from '@/widgets/search/fair-v2/fair-search-v2.data'
import { fetchGiftQuizIdeas, saveGiftQuizIdeas } from '../api/gift-quiz'
import { useGiftQuizStore } from '../store'
import {
  GIFT_QUIZ_BUDGETS,
  GIFT_QUIZ_OCCASIONS,
  GIFT_QUIZ_STEPS,
  emptyGiftQuizAnswers,
  formatGiftQuizBudget,
  giftQuizAnswerChips,
  giftQuizAudience,
  giftQuizProgress,
  giftQuizSearchRequest,
  readGiftQuizPersonTaste,
  writeGiftQuizPersonTaste,
  type GiftQuizAnswers,
  type GiftQuizRecipient,
  type GiftQuizStep,
} from '../gift-quiz.data'
import GiftQuizPreview from './GiftQuizPreview.vue'
import GiftQuizWho from './GiftQuizWho.vue'
import './gift-quiz.css'

const store = useGiftQuizStore()
const { t } = useI18n()
const router = useRouter()
const toast = useFairToast()
const compact = useFairCompact()

const peopleQuery = usePeopleQuery()
const stylesQuery = useAvailableStylesQuery()
const colorsQuery = useAvailableColorsQuery()
const brandsQuery = useAvailableBrandsQuery()
const createPersonMutation = useCreatePersonMutation()

const currency = ref('USD')
const step = ref<GiftQuizStep>('welcome')

/* ── Who ──────────────────────────────────────────────────────────────── */

const people = computed<Person[]>(() => fairProfileV2VisiblePeople(peopleQuery.data.value ?? []))
const selectedId = ref<number | null>(null)
const draft = ref<FairProfileV2PersonDraft>(emptyFairProfileV2PersonDraft())
const recipient = ref<GiftQuizRecipient>({ id: null, name: '', audience: null })
const saving = ref(false)

/** New person: name, relation, age range and gender are required (03 / 04 notes). */
const canContinueWho = computed(
  () => selectedId.value !== null || (isFairProfileV2PersonComplete(draft.value) && Boolean(draft.value.gender)),
)

async function continueWho() {
  const chosen = people.value.find((person) => person.id === selectedId.value)
  if (chosen) {
    recipient.value = { id: chosen.id, name: chosen.profileName, audience: giftQuizAudience(chosen.gender) }
  } else {
    // A new person is added to People (R204).
    saving.value = true
    try {
      const created = await createPersonMutation.mutateAsync(toFairProfileV2PersonPayload(draft.value))
      const id = created?.id ?? null
      if (id !== null && (draft.value.gender === 'girl' || draft.value.gender === 'boy')) {
        writeFairProfileV2ChildSex(id, draft.value.gender)
      }
      recipient.value = {
        id,
        name: draft.value.name.trim(),
        audience: giftQuizAudience(toFairProfileV2PersonPayload(draft.value).gender),
      }
    } catch (error) {
      console.error('Gift quiz: person not added', error)
      toast.show({ tone: 'warning', message: t('gift_quiz.who.save_error') })
      return
    } finally {
      saving.value = false
    }
  }
  prefillTaste()
  goTo('occasion')
}

/* ── Answers ──────────────────────────────────────────────────────────── */

const answers = ref<GiftQuizAnswers>(emptyGiftQuizAnswers())
const stylePicks = ref<number[]>([])
const colorPicks = ref<number[]>([])
const brandPicks = ref<number[]>([])

/** "Prefilled from Anna's card" — the last gift's picks for this person (BE-153). */
function prefillTaste() {
  const taste = readGiftQuizPersonTaste(recipient.value.id)
  stylePicks.value = taste?.styleIds ?? []
  colorPicks.value = taste?.colorIds ?? []
  brandPicks.value = taste?.brandIds ?? []
}

const styleItems = computed<FairProfileV2PickerItem[]>(() =>
  (stylesQuery.data.value ?? []).map((style) => ({ id: style.id, name: style.name, image: style.image ?? undefined })),
)
const colorItems = computed<FairProfileV2PickerItem[]>(() =>
  (colorsQuery.data.value ?? []).map((color) => ({ id: color.id, name: color.name, hex: color.hex })),
)
const brandItems = computed<FairProfileV2PickerItem[]>(() =>
  (brandsQuery.data.value ?? []).map((brand) => ({ id: brand.id, name: brand.name, image: brand.logo })),
)

function namesOf(items: FairProfileV2PickerItem[], ids: number[]): string[] {
  return ids.map((id) => items.find((item) => item.id === id)?.name).filter((name): name is string => Boolean(name))
}

const occasionLabel = computed(() =>
  answers.value.occasion ? t(`gift_quiz.occasion.options.${answers.value.occasion}`) : '',
)
const budgetLabels = computed(() => ({ under: t('gift_quiz.budget.under'), notSure: t('gift_quiz.budget.not_sure') }))
const budgetLabel = computed(() =>
  answers.value.budget ? formatGiftQuizBudget(answers.value.budget, currency.value, budgetLabels.value) : '',
)
const chips = computed(() =>
  giftQuizAnswerChips(
    { ...answers.value, styleNames: namesOf(styleItems.value, stylePicks.value) },
    occasionLabel.value,
    budgetLabel.value,
  ),
)

/* ── Search ───────────────────────────────────────────────────────────── */

const cards = ref<FairSearchV2ProductCard[]>([])
let abort: AbortController | null = null

async function findGifts() {
  writeGiftQuizPersonTaste(recipient.value.id, {
    styleIds: stylePicks.value,
    colorIds: colorPicks.value,
    brandIds: brandPicks.value,
  })
  const full: GiftQuizAnswers = {
    ...answers.value,
    styleNames: namesOf(styleItems.value, stylePicks.value),
    colorNames: namesOf(colorItems.value, colorPicks.value),
    brandIds: [...brandPicks.value],
    brandNames: namesOf(brandItems.value, brandPicks.value),
  }
  step.value = 'loading'
  abort?.abort()
  abort = new AbortController()
  try {
    const response = await fetchGiftQuizIdeas(
      giftQuizSearchRequest(recipient.value, full, currency.value, occasionLabel.value),
      abort.signal,
    )
    cards.value = (response.results?.items ?? []).map(mapFairSearchV2TeaserToGridCard)
    if (!cards.value.length) {
      step.value = 'empty'
      return
    }
    store.setResult({ recipientName: recipient.value.name, chips: chips.value, cards: cards.value })
    step.value = 'preview'
  } catch (error) {
    if ((error as { name?: string })?.name === 'CanceledError' || abort?.signal.aborted) return
    console.error('Gift quiz: ideas did not load', error)
    step.value = 'error'
  }
}

/** X on Loading cancels the search (12 note). */
function cancelSearch() {
  abort?.abort()
  goTo('note')
}

/* ── Result actions ───────────────────────────────────────────────────── */

const savingList = ref(false)

async function saveToList() {
  if (savingList.value) return
  savingList.value = true
  const name = t('gift_quiz.list_name', { name: recipient.value.name })
  try {
    await saveGiftQuizIdeas(name, cards.value.map((card) => card.variantId))
    toast.show({ tone: 'success', message: t('gift_quiz.preview.saved', { name }) })
  } catch (error) {
    console.error('Gift quiz: list not saved', error)
    toast.show({ tone: 'warning', message: t('gift_quiz.preview.save_error') })
  } finally {
    savingList.value = false
  }
}

async function seeAll() {
  store.close()
  await settleFairQuizHistory()
  await router.push({ name: 'gift-ideas' })
}

/** 14 Change answers: back to step 1 with the answers kept. */
function changeAnswers() {
  goTo('who')
}

/* ── Flow ─────────────────────────────────────────────────────────────── */

function goTo(next: GiftQuizStep) {
  step.value = next
}

function move(by: 1 | -1) {
  const index = GIFT_QUIZ_STEPS.indexOf(step.value)
  const next = GIFT_QUIZ_STEPS[Math.max(0, Math.min(GIFT_QUIZ_STEPS.length - 1, index + by))]
  if (next) goTo(next)
}

const progress = computed(() => giftQuizProgress(step.value))
const headerRight = computed(() =>
  ['styles', 'colors', 'brands', 'note'].includes(step.value) ? 'skip' : 'none',
)
const leaveOpen = ref(false)

function requestClose() {
  if (step.value === 'loading') {
    cancelSearch()
    return
  }
  if (['welcome', 'preview', 'empty', 'error'].includes(step.value)) {
    store.close()
    return
  }
  leaveOpen.value = true
}

function exit() {
  leaveOpen.value = false
  store.close()
}

function skip() {
  if (step.value === 'note') {
    void findGifts()
    return
  }
  move(1)
}

/* ── Open ─────────────────────────────────────────────────────────────── */

watch(
  () => store.session,
  async () => {
    step.value = 'welcome'
    currency.value = readStoredCurrency()
    // Occasion, budget and note are asked every time (16 note).
    answers.value = emptyGiftQuizAnswers()
    draft.value = emptyFairProfileV2PersonDraft()
    cards.value = []
    selectedId.value = store.presetPersonId
    await peopleQuery.refresh(true)
  },
  { immediate: true },
)
</script>

<template>
  <FairQuizLayer
    :open="store.isOpen"
    class="gift-quiz"
    :class="`gift-quiz--${step}`"
    :aria-label="t('gift_quiz.aria')"
    @close-request="requestClose"
  >
    <FairQuizHeader
      :progress="progress"
      :right="headerRight"
      :skip-label="t('quiz.skip')"
      :close-label="t('quiz.close')"
      :progress-label="t('quiz.progress')"
      @close="requestClose"
      @skip="skip"
    />

    <FairQuizWelcome
      v-if="step === 'welcome'"
      :title="t('gift_quiz.welcome.title')"
      :subtitle="t('gift_quiz.welcome.subtitle')"
      :pills="[{ label: t('gift_quiz.welcome.pill_time'), star: false }]"
      :continue-label="t('quiz.continue')"
      @continue="goTo('who')"
    />

    <!-- 01–04 Who -->
    <FairQuizStep
      v-else-if="step === 'who'"
      fill
      :title="t('gift_quiz.who.title')"
      :subtitle="people.length ? t('gift_quiz.who.subtitle') : t('gift_quiz.who.subtitle_empty')"
      :show-back="false"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!canContinueWho"
      :continue-loading="saving"
      @continue="continueWho"
    >
      <GiftQuizWho
        v-model:selected-id="selectedId"
        v-model:draft="draft"
        :people="people"
        :compact="compact"
      />
    </FairQuizStep>

    <!-- 05 / 06 Occasion (Other opens a text field) -->
    <FairQuizStep
      v-else-if="step === 'occasion'"
      fill
      :title="t('gift_quiz.occasion.title')"
      :subtitle="t('gift_quiz.occasion.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!answers.occasion"
      @back="goTo('who')"
      @continue="goTo('budget')"
    >
      <div class="gift-quiz__content">
        <div
          class="gift-quiz__cards"
          role="radiogroup"
          :aria-label="t('gift_quiz.occasion.title')"
        >
          <FairThemeCard
            v-for="option in GIFT_QUIZ_OCCASIONS"
            :key="option.value"
            :label="t(`gift_quiz.occasion.options.${option.value}`)"
            :icon="option.icon"
            :selected="answers.occasion === option.value"
            @click="answers = { ...answers, occasion: option.value, occasionText: option.value === 'other' ? answers.occasionText : '' }"
          />
        </div>
        <FairInputText
          v-if="answers.occasion === 'other'"
          v-model="answers.occasionText"
          variant="underline"
          :placeholder="t('gift_quiz.occasion.custom')"
        />
      </div>
    </FairQuizStep>

    <!-- 07 Budget: same cards without icons -->
    <FairQuizStep
      v-else-if="step === 'budget'"
      fill
      :title="t('gift_quiz.budget.title')"
      :subtitle="t('gift_quiz.budget.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!answers.budget"
      @back="goTo('occasion')"
      @continue="goTo('styles')"
    >
      <div
        class="gift-quiz__cards"
        role="radiogroup"
        :aria-label="t('gift_quiz.budget.title')"
      >
        <FairThemeCard
          v-for="option in GIFT_QUIZ_BUDGETS"
          :key="option.value"
          :label="formatGiftQuizBudget(option.value, currency, budgetLabels)"
          :selected="answers.budget === option.value"
          @click="answers = { ...answers, budget: option.value }"
        />
      </div>
    </FairQuizStep>

    <!-- 08–10 the person's Styles / Colors / Brands -->
    <FairQuizStep
      v-else-if="step === 'styles' || step === 'colors' || step === 'brands'"
      fill
      :class="{ 'style-quiz__styles': step === 'styles' }"
      :title="t(`gift_quiz.${step}.title`, { name: recipient.name })"
      :subtitle="t('gift_quiz.pick_any')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      @back="move(-1)"
      @continue="move(1)"
    >
      <FairProfileV2SelectionPicker
        v-if="step === 'styles'"
        v-model="stylePicks"
        kind="styles"
        :items="styleItems"
        :loading="stylesQuery.isLoading.value"
        :error="Boolean(stylesQuery.error.value)"
        @retry="stylesQuery.refresh(true)"
      />
      <FairProfileV2SelectionPicker
        v-else-if="step === 'colors'"
        v-model="colorPicks"
        kind="colors"
        :items="colorItems"
        :loading="colorsQuery.isLoading.value"
        :error="Boolean(colorsQuery.error.value)"
        @retry="colorsQuery.refresh(true)"
      />
      <FairProfileV2SelectionPicker
        v-else
        v-model="brandPicks"
        kind="brands"
        :items="brandItems"
        :loading="brandsQuery.isLoading.value"
        :error="Boolean(brandsQuery.error.value)"
        @retry="brandsQuery.refresh(true)"
      />
    </FairQuizStep>

    <!-- 11 Note -->
    <FairQuizStep
      v-else-if="step === 'note'"
      fill
      :title="t('gift_quiz.note.title')"
      :subtitle="t('gift_quiz.note.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('gift_quiz.note.find')"
      @back="goTo('brands')"
      @continue="findGifts"
    >
      <FairInputText
        v-model="answers.note"
        variant="underline"
        :placeholder="t('gift_quiz.note.placeholder')"
      />
    </FairQuizStep>

    <!-- 12 Loading: skeleton, × cancels -->
    <FairQuizStep
      v-else-if="step === 'loading'"
      fill
      :title="t('gift_quiz.loading.title')"
      :subtitle="t('gift_quiz.loading.subtitle', { name: recipient.name })"
      :show-footer="false"
    >
      <div
        class="gift-quiz__skeleton"
        aria-busy="true"
      >
        <FairSkeleton
          v-for="index in 3"
          :key="index"
          class="gift-quiz__skeleton-card"
          variant="media"
          :mark="false"
        />
      </div>
    </FairQuizStep>

    <!-- 13 Preview -->
    <GiftQuizPreview
      v-else-if="step === 'preview'"
      :name="recipient.name"
      :chips="chips"
      :cards="cards"
      :compact="compact"
      :saving="savingList"
      @save="saveToList"
      @see-all="seeAll"
    />

    <!-- 14 No ideas -->
    <FairQuizStep
      v-else-if="step === 'empty'"
      :title="t('gift_quiz.empty.title')"
      :subtitle="t('gift_quiz.empty.subtitle')"
      :show-footer="false"
    >
      <div class="gift-quiz__empty">
        <div class="gift-quiz__chips">
          <FairChip
            v-for="chip in chips"
            :key="chip"
            :label="chip"
            :size="compact ? 'mobile' : 'default'"
          />
        </div>
        <FairButton
          class="gift-quiz__change"
          variant="tonal"
          size="auto"
          :label="t('gift_quiz.empty.change')"
          @click="changeAnswers"
        />
      </div>
    </FairQuizStep>

    <!-- 15 Error -->
    <FairQuizStep
      v-else-if="step === 'error'"
      :title="t('gift_quiz.error.title')"
      :subtitle="t('gift_quiz.error.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('gift_quiz.error.retry')"
      @back="goTo('note')"
      @continue="findGifts"
    />

    <FairQuizLeaveDialog
      v-model:visible="leaveOpen"
      :title="t('gift_quiz.leave.title')"
      :text="t('gift_quiz.leave.text')"
      :keep-label="t('gift_quiz.leave.keep')"
      :exit-label="t('gift_quiz.leave.exit')"
      :close-label="t('quiz.close')"
      @exit="exit"
    />
  </FairQuizLayer>
</template>
